import { useEffect, useRef, type ReactNode, type JSX } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './authStore';

/**
 * Route guard. Boots silent-refresh on first render and redirects to /login if unauthenticated.
 *
 * The hydratingRef guard prevents React 18 Strict Mode's double-effect invocation from
 * calling hydrateOnBoot() twice with the same refresh token — which would trigger the
 * backend's reuse-detection and terminate all sessions unexpectedly.
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const status = useAuthStore((s) => s.status);
  const hydrate = useAuthStore((s) => s.hydrateOnBoot);
  const hydratingRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (status === 'unknown' && !hydratingRef.current) {
      hydratingRef.current = true;
      void hydrate().finally(() => {
        hydratingRef.current = false;
      });
    }
  }, [status, hydrate]);

  if (status === 'unknown') {
    return (
      <div className="min-h-screen bg-bg-0 text-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/15 border-t-emerald-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (status !== 'authenticated') {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}
